import { motion } from "motion/react";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import { NotificationStatus } from "@/domains/notification/constants/status";

type Props = { className?: string; status?: NotificationStatus };

export default function AchievementGrid({ className, status = NotificationStatus.Success }: Props) {
  return (
    <div className={cn("relative flex items-start gap-2.5", className)}>
      <motion.div
        initial={{ opacity: 0, width: 0 }}
        animate={{ opacity: 1, width: "15.625rem" }}
        transition={{
          default: {
            duration: 1,
            delay: 0.2,
            type: "spring",
            stiffness: 100,
          },
          opacity: {
            duration: 0.3,
          },
        }}
        exit={{ opacity: 0, width: 0 }}
        className={clsx(
          "absolute h-1 bg-gradient-to-r from-transparent via-50% to-transparent left-[50%] -translate-x-[50%]",
          {
            "via-[#84FF97]": status === NotificationStatus.Success,
            "via-[#FF4E4E]": status === NotificationStatus.Error,
            "via-[#5A99FF]": status === NotificationStatus.Info,
            "via-white": status === NotificationStatus.Inherit,
          },
        )}
      />
      <motion.div
        initial={{
          boxShadow: {
            [NotificationStatus.Success]: "0 0 0 0 #84FF9755",
            [NotificationStatus.Error]: "0 0 0 0 #FF4E4E55",
            [NotificationStatus.Info]: "0 0 0 0 #5A99FF55",
            [NotificationStatus.Inherit]: "0 0 0 0 #FFFFFF55",
          }[status],
        }}
        animate={{
          boxShadow: {
            [NotificationStatus.Success]: "0 0 280px 200px #84FF9755",
            [NotificationStatus.Error]: "0 0 280px 200px #FF4E4E55",
            [NotificationStatus.Info]: "0 0 280px 200px #5A99FF55",
            [NotificationStatus.Inherit]: "0 0 280px 200px #FFFFFF55",
          }[status],
        }}
        exit={{
          opacity: 0,
        }}
        transition={{
          type: "spring",
          duration: 1,
        }}
        className="absolute left-[50%] -top-[70px]"
      />

      <motion.div
        initial={{ translateY: "-100%", opacity: 0.4 }}
        animate={{ translateY: "none", opacity: 1 }}
        transition={{
          default: {
            duration: 0.2,
          },
          opacity: {
            duration: 1,
            repeatType: "reverse",
            repeat: Infinity,
          },
        }}
        exit={{ translateY: "-100%" }}
        className={clsx("w-[1.25rem] h-[1.25rem] bg-gradient-to-b to-transparent", {
          "from-[#84FF97]/20": status === NotificationStatus.Success,
          "from-[#FF4E4E]/20": status === NotificationStatus.Error,
          "from-[#5A99FF]/20": status === NotificationStatus.Info,
          "from-white/20": status === NotificationStatus.Inherit,
        })}
      />
      <motion.div
        initial={{ translateY: "-100%", opacity: 0.4 }}
        animate={{ translateY: "none", opacity: 1 }}
        exit={{ translateY: "-100%" }}
        transition={{
          default: {
            duration: 0.2,
            delay: 0.1,
            type: "spring",
            stiffness: 50,
          },
          opacity: {
            delay: 0.1,
            duration: 1,
            repeatType: "reverse",
            repeat: Infinity,
          },
        }}
        className={clsx("w-[1.25rem] h-[1.875rem] bg-gradient-to-b to-transparent", {
          "from-[#84FF97]/40": status === NotificationStatus.Success,
          "from-[#FF4E4E]/40": status === NotificationStatus.Error,
          "from-[#5A99FF]/40": status === NotificationStatus.Info,
          "from-white/40": status === NotificationStatus.Inherit,
        })}
      />
      <motion.div
        initial={{ translateY: "-100%", opacity: 0.4 }}
        animate={{ translateY: "none", opacity: 1 }}
        exit={{ translateY: "-100%" }}
        transition={{
          default: {
            duration: 0.2,
            delay: 0.2,
            type: "spring",
            stiffness: 50,
          },
          opacity: {
            duration: 1,
            delay: 0.2,
            repeatType: "reverse",
            repeat: Infinity,
          },
        }}
        className={clsx("w-[1.25rem] h-[2.625rem] bg-gradient-to-b to-transparent", {
          "from-[#84FF97]/40": status === NotificationStatus.Success,
          "from-[#FF4E4E]/40": status === NotificationStatus.Error,
          "from-[#5A99FF]/40": status === NotificationStatus.Info,
          "from-white/40": status === NotificationStatus.Inherit,
        })}
      />
      <motion.div
        initial={{ translateY: "-100%", opacity: 0.4 }}
        animate={{ translateY: "none", opacity: 1 }}
        exit={{ translateY: "-100%" }}
        transition={{
          default: {
            duration: 0.2,
            delay: 0.3,
            type: "spring",
            stiffness: 50,
          },
          opacity: {
            duration: 1,
            delay: 0.3,
            repeatType: "reverse",
            repeat: Infinity,
          },
        }}
        className={clsx("w-[1.25rem] h-[4.625rem] bg-gradient-to-b to-transparent", {
          "from-[#84FF97]/30": status === NotificationStatus.Success,
          "from-[#FF4E4E]/30": status === NotificationStatus.Error,
          "from-[#5A99FF]/30": status === NotificationStatus.Info,
          "from-white/30": status === NotificationStatus.Inherit,
        })}
      />
      <motion.div
        initial={{ translateY: "-100%", opacity: 0.4 }}
        animate={{ translateY: "none", opacity: 1 }}
        exit={{ translateY: "-100%" }}
        transition={{
          default: {
            duration: 0.2,
            delay: 0.4,
            type: "spring",
            stiffness: 50,
          },
          opacity: {
            duration: 1,
            delay: 0.4,
            repeatType: "reverse",
            repeat: Infinity,
          },
        }}
        className={clsx("w-[1.25rem] h-[3.375rem] bg-gradient-to-b to-transparent", {
          "from-[#84FF97]/10": status === NotificationStatus.Success,
          "from-[#FF4E4E]/10": status === NotificationStatus.Error,
          "from-[#5A99FF]/10": status === NotificationStatus.Info,
          "from-white/10": status === NotificationStatus.Inherit,
        })}
      />
      <motion.div
        initial={{ translateY: "-100%", opacity: 0.4 }}
        animate={{ translateY: "none", opacity: 1 }}
        exit={{ translateY: "-100%" }}
        transition={{
          default: {
            duration: 0.2,
            delay: 0.4,
            type: "spring",
            stiffness: 50,
          },
          opacity: {
            duration: 1,
            delay: 0.5,
            repeatType: "reverse",
            repeat: Infinity,
          },
        }}
        className={clsx("w-[1.25rem] h-[3.375rem] bg-gradient-to-b to-transparent", {
          "from-[#84FF97]/10": status === NotificationStatus.Success,
          "from-[#FF4E4E]/10": status === NotificationStatus.Error,
          "from-[#5A99FF]/10": status === NotificationStatus.Info,
          "from-white/10": status === NotificationStatus.Inherit,
        })}
      />
      <motion.div
        initial={{ translateY: "-100%", opacity: 0.4 }}
        animate={{ translateY: "none", opacity: 1 }}
        exit={{ translateY: "-100%" }}
        transition={{
          default: {
            duration: 0.2,
            delay: 0.3,
            type: "spring",
            stiffness: 50,
          },
          opacity: {
            duration: 1,
            delay: 0.6,
            repeatType: "reverse",
            repeat: Infinity,
          },
        }}
        className={clsx("w-[1.25rem] h-[4.625rem] bg-gradient-to-b to-transparent", {
          "from-[#84FF97]/30": status === NotificationStatus.Success,
          "from-[#FF4E4E]/30": status === NotificationStatus.Error,
          "from-[#5A99FF]/30": status === NotificationStatus.Info,
          "from-white/30": status === NotificationStatus.Inherit,
        })}
      />
      <motion.div
        initial={{ translateY: "-100%", opacity: 0.4 }}
        animate={{ translateY: "none", opacity: 1 }}
        exit={{ translateY: "-100%" }}
        transition={{
          default: {
            duration: 0.2,
            delay: 0.2,
            type: "spring",
            stiffness: 50,
          },
          opacity: {
            duration: 1,
            delay: 0.7,
            repeatType: "reverse",
            repeat: Infinity,
          },
        }}
        className={clsx("w-[1.25rem] h-[2.625rem] bg-gradient-to-b to-transparent", {
          "from-[#84FF97]/40": status === NotificationStatus.Success,
          "from-[#FF4E4E]/40": status === NotificationStatus.Error,
          "from-[#5A99FF]/40": status === NotificationStatus.Info,
          "from-white/40": status === NotificationStatus.Inherit,
        })}
      />
      <motion.div
        initial={{ translateY: "-100%", opacity: 0.4 }}
        animate={{ translateY: "none", opacity: 1 }}
        exit={{ translateY: "-100%" }}
        transition={{
          default: {
            duration: 0.2,
            delay: 0.1,
            type: "spring",
            stiffness: 50,
          },
          opacity: {
            duration: 1,
            delay: 0.8,
            repeatType: "reverse",
            repeat: Infinity,
          },
        }}
        className={clsx("w-[1.25rem] h-[1.875rem] bg-gradient-to-b  to-transparent", {
          "from-[#84FF97]/40": status === NotificationStatus.Success,
          "from-[#FF4E4E]/40": status === NotificationStatus.Error,
          "from-[#5A99FF]/40": status === NotificationStatus.Info,
          "from-white/40": status === NotificationStatus.Inherit,
        })}
      />
      <motion.div
        initial={{ translateY: "-100%", opacity: 0.8 }}
        animate={{ translateY: "none", opacity: 1 }}
        exit={{ translateY: "-100%" }}
        transition={{
          default: {
            duration: 0.2,
            type: "spring",
            stiffness: 50,
          },
          opacity: {
            duration: 1,
            delay: 0.9,
            repeatType: "reverse",
            repeat: Infinity,
          },
        }}
        className={clsx("w-[1.25rem] h-[1.25rem] bg-gradient-to-b to-transparent", {
          "from-[#84FF97]/20": status === NotificationStatus.Success,
          "from-[#FF4E4E]/20": status === NotificationStatus.Error,
          "from-[#5A99FF]/20": status === NotificationStatus.Info,
          "from-white/20": status === NotificationStatus.Inherit,
        })}
      />
    </div>
  );
}
